import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Controller, type Control, type FieldErrors, type UseFormRegister } from "react-hook-form";
import { sortCzech } from "@ckgo2/shared";
import { reviewSchema, type ReviewCreateInput } from "@ckgo2/shared/schemas";
import { api, type ApiResponse } from "@/api/client";
import type { Trip, Guide } from "@ckgo2/shared/types";
import { ArrowLeft, Save, Loader2, Star } from "lucide-react";
import MediaPicker from "@/components/common/MediaPicker";
import ErrorBanner from "@/components/common/ErrorBanner";
import ValidationErrorBanner from "@/components/common/ValidationErrorBanner";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import DestinationPicker from "@/components/common/DestinationPicker";
import { useEditPage } from "@/hooks/useEditPage";
import {
  REVIEW_FORM_DEFAULTS,
  reviewFormFromDetail,
  reviewSaveBody,
  type ReviewDetail,
} from "./reviewForm";

function ReviewCustomerSection({
  register,
  errors,
}: {
  register: UseFormRegister<ReviewCreateInput>;
  errors: FieldErrors<ReviewCreateInput>;
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        {t("content.reviews.form.customerInfoTitle")}
      </h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="customerName" className="label">
            {t("content.reviews.form.customerNameLabel")} *
          </label>
          <input
            {...register("customerName")}
            type="text"
            id="customerName"
            className={`input ${errors.customerName ? "input-error" : ""}`}
            placeholder={t("content.reviews.form.customerNamePlaceholder")}
          />
          {errors.customerName && (
            <p className="text-error-600 mt-1 text-sm">{errors.customerName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="customerLocation" className="label">
            {t("content.reviews.form.customerLocationLabel")}
          </label>
          <input
            {...register("customerLocation")}
            type="text"
            id="customerLocation"
            className="input"
            placeholder={t("content.reviews.form.customerLocationPlaceholder")}
          />
        </div>
      </div>
    </div>
  );
}

function ReviewContentSection({
  register,
  errors,
  rating,
  onRatingChange,
}: {
  register: UseFormRegister<ReviewCreateInput>;
  errors: FieldErrors<ReviewCreateInput>;
  rating: number;
  onRatingChange: (value: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        {t("content.reviews.form.contentTitle")}
      </h2>

      <div className="space-y-4">
        <div>
          <label className="label">{t("content.reviews.form.ratingLabel")} *</label>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => onRatingChange(value)}
                className="p-1"
              >
                <Star
                  className={`h-8 w-8 transition-colors ${
                    value <= rating
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-gray-300 hover:text-yellow-300"
                  }`}
                />
              </button>
            ))}
            <span className="ml-2 text-sm text-gray-500">
              {t("content.reviews.form.ratingOf", { rating })}
            </span>
          </div>
        </div>

        <div>
          <label htmlFor="title" className="label">
            {t("content.reviews.form.reviewTitleLabel")}
          </label>
          <input
            {...register("title")}
            type="text"
            id="title"
            className="input"
            placeholder={t("content.reviews.form.reviewTitlePlaceholder")}
          />
        </div>

        <div>
          <label htmlFor="content" className="label">
            {t("content.reviews.form.reviewTextLabel")} *
          </label>
          <textarea
            {...register("content")}
            id="content"
            rows={6}
            className={`input ${errors.content ? "input-error" : ""}`}
            placeholder={t("content.reviews.form.reviewTextPlaceholder")}
          />
          {errors.content && (
            <p className="text-error-600 mt-1 text-sm">{errors.content.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="tripDate" className="label">
            {t("content.reviews.form.tripDateLabel")}
          </label>
          <input
            {...register("tripDate", { valueAsDate: true })}
            type="date"
            id="tripDate"
            className="input"
          />
        </div>
      </div>
    </div>
  );
}

function ReviewSidebar({
  register,
  control,
  trips,
  guides,
}: {
  register: UseFormRegister<ReviewCreateInput>;
  control: Control<ReviewCreateInput>;
  trips: Trip[];
  guides: Guide[];
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          {t("content.reviews.form.connectionsTitle")}
        </h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="tripId" className="label">
              {t("common.fields.trip")}
            </label>
            <select {...register("tripId")} id="tripId" className="input">
              <option value="">{t("content.reviews.form.selectTrip")}</option>
              {trips.map((trip) => (
                <option key={trip.id} value={trip.id}>
                  {trip.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="guideId" className="label">
              {t("content.reviews.form.guideLabel")}
            </label>
            <select {...register("guideId")} id="guideId" className="input">
              <option value="">{t("content.reviews.form.selectGuide")}</option>
              {guides.map((guide) => (
                <option key={guide.id} value={guide.id}>
                  {guide.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="destinationId" className="label">
              {t("common.fields.destination")}
            </label>
            <Controller
              name="destinationId"
              control={control}
              render={({ field }) => (
                <DestinationPicker
                  id="destinationId"
                  value={field.value}
                  onChange={(id) => field.onChange(id ?? "")}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          {t("common.settings")}
        </h2>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              {...register("isApproved")}
              type="checkbox"
              id="isApproved"
              className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
            />
            <label
              htmlFor="isApproved"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t("content.reviews.form.approvedForDisplay")}
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              {...register("isFeatured")}
              type="checkbox"
              id="isFeatured"
              className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
            />
            <label
              htmlFor="isFeatured"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t("content.reviews.form.showAsFeatured")}
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReviewEditPage() {
  const { t } = useTranslation();

  // Recenze nemá slug (`generateSlug: false`) a její `PUT` nemění `updatedAt`: refetch po
  // uložení se smazanou vazbou vrátí tatáž data a formulář se k nim musí vrátit i tak
  // (`resetOnRefetch: true`). Uložení, chyby polí i přechod nové recenze na detail dělá hook.
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    ReviewCreateInput,
    ReviewDetail
  >({
    resourceKey: "review",
    listKey: "reviews",
    apiPath: "/api/admin/reviews",
    schema: reviewSchema,
    defaultValues: REVIEW_FORM_DEFAULTS,
    navigateTo: "/reviews",
    generateSlug: false,
    resetOnRefetch: true,
    transformOnLoad: reviewFormFromDetail,
    transformOnSave: reviewSaveBody,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // Fetch trips for select. Own key: `["trips"]` holds the ADMIN trip list elsewhere (TripsPage,
  // BlogPostEditPage, …), and this public list must neither overwrite nor be overwritten by it.
  const { data: tripsData } = useQuery({
    queryKey: ["review-trip-options"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/trips"),
  });

  // Fetch guides for select
  const { data: guidesData } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  // Výběry zájezdu a průvodce česky abecedně (F1.8); `sortCzech` vrací kopii, keš se nemění.
  // Destinaci vybírá sdílený `DestinationPicker` (i nepublikované jako „koncept").
  const trips = sortCzech(tripsData?.data || [], (trip) => trip.title);
  const guides = sortCzech(guidesData?.data || [], (guide) => guide.name);

  const rating = watch("rating");
  const isVideo = watch("isVideo");

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/reviews" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? t("content.reviews.form.newTitle") : t("content.reviews.form.editTitle")}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? t("content.reviews.form.newSubtitle")
                : t("content.reviews.form.editSubtitle", { name: itemData?.customerName })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          <button
            type="submit"
            form="review-form"
            disabled={isSubmitting || (!isDirty && !isNew)}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />
      <ValidationErrorBanner errors={errors} />

      {/* Form */}
      <form
        id="review-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-3"
      >
        {/* Main content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          <ReviewCustomerSection register={register} errors={errors} />

          <ReviewContentSection
            register={register}
            errors={errors}
            rating={rating}
            onRatingChange={(value) => setValue("rating", value, { shouldDirty: true })}
          />

          {/* Video */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.reviews.form.videoTitle")}
            </h2>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  {...register("isVideo")}
                  type="checkbox"
                  id="isVideo"
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                />
                <label
                  htmlFor="isVideo"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t("content.reviews.form.isVideoLabel")}
                </label>
              </div>

              {isVideo && (
                <div>
                  <label htmlFor="videoUrl" className="label">
                    {t("content.reviews.form.videoUrlLabel")}
                  </label>
                  <input
                    {...register("videoUrl")}
                    type="url"
                    id="videoUrl"
                    className="input"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Review Photos */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.reviews.form.photosTitle")}
            </h2>
            <p className="mb-4 text-sm text-gray-500">{t("content.reviews.form.photosHint")}</p>
            <MediaPicker
              mode="multiple"
              value={(watch("imageIds") as string[] | undefined) || []}
              onChange={(ids) => setValue("imageIds", ids, { shouldDirty: true })}
              folder="general"
            />
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <ReviewSidebar register={register} control={control} trips={trips} guides={guides} />
      </form>
    </div>
  );
}
